export interface Facet {
  field_name: string;
  counts: Bucket[];
  stats: { total_values: number; min?: number; max?: number };
}

export interface Bucket {
  value: string;
  count: number;
  parent?: unknown;
}

export interface BucketWithParent<T> {
  value: string;
  count: number;
  parent?: T;
}

export interface FacetedOption {
  value: string | number | boolean | null;
  label: string;
  count?: number;
  disable?: boolean;
}

export interface Brand {
  id: string;
  name: string;
  slug: string;
  char: string;
  description?: string;
  imageUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
  shortDescription?: string;
  hasRebates: boolean;
  updatedAt: number;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  path: string;
  order?: string;
  description?: string;
  imageUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
  shortDescription?: string;
  /** Null value is `__null__` */
  parentId: string;
  childCount: number;
  updatedAt: number;
  ancestors: {
    id: number;
    name: string;
    slug: string;
    parentId: number | null;
    level: number;
  }[];
}

export interface CategoryBrand {
  id: string;
  name: string;
  slug: string;
  categoryId: string;
  brandId: string;
  imageUrl?: string;
}

export type SpecialOrder = 'Preorder' | 'Backorder';

export type Condition = 'New' | 'Used' | 'Blemished';

export interface ProductFacet {
  value: string;
  label: string;
  order: number;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  slug: string;
  style: string;
  mpn: string;
  upc: string;
  condition: Condition;
  description?: string;
  shortDescription?: string;
  longDescription?: string;
  keywords: string[];
  imageUrl?: string;
  thumbnailUrl?: string;
  placeholderUrl?: string;
  price: number;
  regularPrice: number;
  unitPrice?: number;
  mapPrice: number;
  mapPolicy?: string;
  orderType?: 'Notify' | SpecialOrder;
  quantitySold: number;
  orderCount: number;
  averageScore: number;
  reviewCount: number;
  isOnSale: boolean;
  isAmmunition: boolean;
  isClass3: boolean;
  isFirearm: boolean;
  isFreeShipping: boolean;
  isHazmat: boolean;
  isInStock: boolean;
  isInStore: boolean;
  isPartnerFulfilled: boolean;
  isQualifiedProfessional: boolean;
  isUsed: boolean;
  isVariable: boolean;
  isVirtual: boolean;
  hasRebates: boolean;
  action?: string;
  model?: string;
  barrelLength?: number;
  caliber: string[];
  capacity?: number;
  finish?: string;
  hand?: string;
  warehouses: string[];
  manufacturer?: {
    id: number;
    name: string;
    slug: string;
  };
  categories: {
    id: number;
    name: string;
    slug: string;
    parentId: string | null;
    level: number;
  }[];
  groups: {
    id: number;
    name: string;
    slug: string;
  }[];
  priceFacet: ProductFacet;
  updatedAt: number;
}

export interface Article {
  id: string;
  title: string;
  slug: string;
  author?: string;
  content: string;
  thumbnailUrl?: string;
  thumbnailPlaceholder?: string;
  createdAt: number;
  updatedAt: number;
  publishedAt: number;
}

export interface Query {
  id: string;
  q: string;
  count: number;
}

export class NumberRange {
  constructor(
    public min?: number,
    public max?: number,
  ) {}

  public static create(min?: number, max?: number) {
    return new NumberRange(min, max);
  }

  public valid() {
    return this.first() !== undefined;
  }

  public same() {
    return this.valid() && this.min == this.max;
  }

  public first() {
    return this.min ?? this.max;
  }
}
